// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://ai.webwonders.co.in/api' ;
export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;

// API Endpoints https://ai.webwonders.co.in
export const API_ENDPOINTS = {
  // Authentication
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    changePassword: '/auth/change-password'
  },
  
  // Sessions & Audit
  sessions: {
    active: '/sessions/active',
    userSessions: (userId) => `/sessions/user/${userId}`,
    terminate: (sessionId) => `/sessions/terminate/${sessionId}`,
    terminateUser: (userId) => `/sessions/terminate-user/${userId}`,
    auditLogs: '/sessions/audit-logs',
    auditRecordHistory: '/sessions/audit-logs/record',
    auditLogsExport: '/sessions/audit-logs/export',
    auditSummary: '/sessions/audit-summary',
    auditEmployeeSummary: '/sessions/audit-employee-summary',
    auditLeaderboard: '/sessions/audit-leaderboard',
    statistics: '/sessions/statistics'
  },
  
  // Users & Employees
  users: {
    profile: '/users/profile',
    employees: '/users/employees',
    allEmployees: '/users/allemployees',
    addEmployee: '/users/add-employee',
    updateEmployee: (id) => `/users/employee/${id}`,
    addProfile: '/users/add-profile',
    employeesProperty: '/users/employeesproperty'
  },
  
  // Leads
  leads: {
    base: '/leads',
    calendar: '/leads/calender',
    add: '/leads/add',
    detail: (id) => `/leads/detail/${id}`,
    publicSubmit: '/leads/public',
    update: (id) => `/leads/update/${id}`,
    delete: (id) => `/leads/delete/${id}`,
    convertToClient: (id) => `/leads/${id}/convert-to-client`,
    duplicates: (id) => `/leads/${id}/duplicates`,
    merge: '/leads/merge',
    updateStatus: (id) => `/leads/updateStatus/${id}`,
    assignEmployee: (id) => `/leads/asignleademployee/${id}`,
    getLeadEmployee: '/leads/getleademployeee',
    getAllClosed: '/leads/getAllLeadsClosed',
    markEventAsDone: (oppId, commentId, eventType) => `/leads/markEventAsDone/${oppId}/${commentId}/${eventType}`,
    remindersPreview: '/leads/reminders/preview',
    remindersRun: '/leads/reminders/run',
  },
  
  // Opportunities
  opportunities: {
    base: '/opportunities',
    detail: (id) => `/opportunities/${id}`,
    pipeline: '/opportunities/pipeline',
    visible: '/opportunities/visible',
    pdf: (id) => `/opportunities/pdf/${id}`,
    delete: (id) => `/opportunities/${id}/delete`,
    updateStatus: (id) => `/opportunities/updatestatus/${id}`,
    comment: (id) => `/opportunities/opportunity/${id}/comment`,
    loa: (id) => `/opportunities/${id}/loa`,
    loaAdd: (id) => `/opportunities/${id}/loaadd`,
    agreement: (id) => `/opportunities/${id}/agreement`,
    agreementAdd: (id) => `/opportunities/${id}/agreementadd`,
    siteVisit: (id) => `/opportunities/${id}/site-visit`,
    followUps: (id) => `/opportunities/${id}/follow-ups`,
    verifyProposal: (id) => `/opportunities/verify-proposal/${id}`,
    sendProposalMail: (id) => `/opportunities/send-proposal-mail/${id}`,
    visitors: (id) => `/proposal/${id}/visitors`,
    remindersPreview: '/opportunities/reminders/preview',
    remindersRun: '/opportunities/reminders/run',
  },
  
  // Clients
  clients: {
    base: '/clients',
    byId: (id) => `/clients/client/${id}`,
    opportunities: (id) => `/clients/client/${id}/opportunities`,
    sourceLead: (id) => `/clients/client/${id}/source-lead`,
    assign: (id) => `/clients/${id}/assign`,
    update: (id) => `/clients/${id}`,
    recommend: (id) => `/recommend/recommend/${id}`,
    feFoundProperty: (clientId) => `/clients/${clientId}/fe-found-property`,
    feFoundPropertyEntry: (clientId, propertyId) =>
      `/clients/${clientId}/fe-found-property/${propertyId}`,
    preferences: (clientId) => `/clients/${clientId}/preferences`,
    addPreference: (clientId) => `/clients/${clientId}/preferences`,
    removePreference: (clientId, entryId) => `/clients/${clientId}/preferences/${entryId}`,
  },

  // Properties
  properties: {
    base: '/properties',
    myProperty: '/properties/myproperty',
    archive: '/properties/archive',
    pendingApproval: '/properties/pending-approval',
    brokerageSubmit: '/properties/brokerage/submit',
    approveSubmission: (id) => `/properties/${id}/approve-submission`,
    rejectSubmission: (id) => `/properties/${id}/reject-submission`,
    archiveById: (id) => `/properties/${id}/archive`,
    byId: (id) => `/properties/${id}`,
    recommend: (id) => `/recommend/recommend/property/${id}`,
    unarchive: (id) => `/properties/${id}/unarchive`,
    roadmap: (id) => `/properties/${id}/roadmap`
  },

  // Links & Property Linking
  links: {
    linkProperty: '/link/link-property',
    overridePreview: '/link/override-preview',
    unlinkProperty: '/link/unlink-property',
    linkClient: (clientId) => `/link/client/${clientId}`
  },
  
  // Proposals
  proposals: {
    trackVisit: '/proposal/track-visit',
    opportunity: (id) => `/proposal/opportunity/${id}`,
    visitors: (id) => `/proposal/${id}/visitors`
  },
  
  // Reports
  reports: {
    count: '/count',
    myReport: '/reports/myreport',
    availableBrands: '/reports/available-brands',
    tagReports: '/reports/tag-reports',
    leadGeneration: '/reports/lead-generation-report',
    leadToOpportunity: '/reports/lead-to-opportunity-report',
    brandWise: '/reports/brand-wise-report',
    cityWise: (city) => `/reports/city-wise-report/${city}`,
    feProperty: '/reports/fe-property-report',
    summary: '/reports/summary-report',
    tatReport: '/reports/tat-report',
    tatIndividualReport: '/reports/tat-individual-report',
    adminDashboardFunnel: '/reports/admin-dashboard-funnel'
  },

  insights: {
    latest: '/insights/latest',
    history: '/insights/history',
    generate: '/insights/generate',
    runWeekly: '/insights/run-weekly',
    atRisk: '/insights/at-risk',
  },
  
  // Other
  notifications: {
    base: '/notifications',
    count: '/notifications/count',
    markRead: (id) => `/notifications/${id}/read`,
    markAllRead: '/notifications/read-all',
  },

  followUpTasks: {
    base: '/follow-up-tasks',
    summary: '/follow-up-tasks/summary',
    schedules: '/follow-up-tasks/schedules',
    filterOptions: '/follow-up-tasks/filter-options',
    complete: (id) => `/follow-up-tasks/${id}/complete`,
    employeeReports: '/follow-up-tasks/reports/employees',
    sync: '/follow-up-tasks/sync',
  },

  settings: {
    reminders: '/settings/reminders',
  },
  
  chat: {
    chat: '/chat/chat'
  },
  
  // Data/Analytics
  analytics: {
    opportunityCount: '/opportunities/lol/count/'
  },
  
  track: {
    getAllLeads: '/track/getallLeads',
    track: (id) => `/track/track/${id}`
  },

  // Agent orchestration
  events: {
    status: '/events/status',
    recent: '/events/recent',
    journey: (correlationId) => `/events/journey/${correlationId}`,
    logsRecent: '/events/logs/recent',
    outboxPending: '/events/outbox/pending',
    outboxReplay: (outboxId) => `/events/outbox/replay/${outboxId}`,
  },

  agents: {
    controlCenter: '/agents/control-center',
    automation: '/agents/automation',
    batchApprove: '/agents/actions/batch-approve',
    pending: '/agents/actions/pending',
    byId: (id) => `/agents/actions/${id}`,
    approve: (id) => `/agents/actions/${id}/approve`,
    reject: (id) => `/agents/actions/${id}/reject`,
    matchingForClient: (clientId) => `/agents/matching/client/${clientId}`,
    resumeAi: (correlationId) => `/agents/journey/${correlationId}/resume-ai`,
  },

  journey: {
    byCorrelationId: (correlationId) => `/journey/${correlationId}`,
    events: (correlationId) => `/journey/${correlationId}/events`,
    summary: (correlationId) => `/journey/${correlationId}/summary`,
  },

  channels: {
    conversation: (correlationId) => `/channels/conversation/${correlationId}`,
    emailInbound: '/channels/email/inbound',
    whatsappInbound: '/channels/whatsapp/inbound',
  },

  intelligence: {
    metrics: '/intelligence/metrics',
    config: '/intelligence/config',
    proposals: '/intelligence/proposals',
    runBatch: '/intelligence/batch/run',
    approveProposal: (id) => `/intelligence/proposals/${id}/approve`,
    rejectProposal: (id) => `/intelligence/proposals/${id}/reject`,
  },

  exceptions: {
    base: '/exceptions',
    summary: '/exceptions/summary',
    resolve: (id) => `/exceptions/${id}/resolve`,
  },

  activities: {
    log: '/activities/log',
    client: (clientId) => `/activities/client/${clientId}`,
    lead: (leadId) => `/activities/lead/${leadId}`,
  },

  analytics: {
    overrides: '/analytics/overrides',
    preferences: '/analytics/preferences',
  },
};

// Helper function to build full URL
export const buildApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint}`;
};